import { IconButton, Stack, TableCell, TableRow, Tooltip, Typography, useTheme } from "@mui/material"
import { StandardCheckBox } from "lib/@components/standard-checkbox"
import { InfoIcon } from "theme/overrides/CustomIcons"

import { FIRST_COL_WIDTH, OTHER_COLS_WIDTH, ROLES_TO_DISPLAY } from "./constants"
import { updateRole } from "lib/permissions/roles"
import { lowercaseAndReplaceSpaceWithHyphens } from "lib/permissions/helpers"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import Iconify from "minimals-template/components/Iconify"

function RolesTableGroupDemandCell({ isSelected, isTopRightCell, isBottomRightCell, role, demand, empty = false }) {
    const { clientId: client } = useBoundContext()
    let cellName
    if (empty) cellName = `empty-cell`
    else {
        cellName = `${lowercaseAndReplaceSpaceWithHyphens(role.name)}-${lowercaseAndReplaceSpaceWithHyphens(
            demand?.name
        )}`
    }
    return (
        <TableCell
            sx={{
                minWidth: OTHER_COLS_WIDTH,
                ...(isTopRightCell && { borderTopRightRadius: "10px" }),
                ...(isBottomRightCell && {
                    borderBottomRightRadius: "10px",
                }),
            }}
            data-cy={`roles-table-cell-${cellName}`}
        >
            {!empty ? (
                <Stack alignItems="center" justifyContent="center" direction="column">
                    <StandardCheckBox
                        data-cy={`roles-table-cell-${cellName}-checkbox`}
                        labelRight={""}
                        value={isSelected}
                        onChange={handleToggleRoleDemand}
                        inputProps={{ "aria-label": "select all rows" }}
                    />
                </Stack>
            ) : (
                <>{/* spacer */} </>
            )}
        </TableCell>
    )

    function handleToggleRoleDemand() {
        handleUpdateRoleDemands(role, demand, client).catch(console.error)
    }
}

export function RolesTableBodyRow({ roles, demand, groupDemandIx, groups, group }) {
    const theme = useTheme()
    const isTopLeftCell = groupDemandIx === 0
    const isBottomLeftCell = groupDemandIx === groups[group].length - 1
    return (
        <TableRow
            sx={{
                backgroundColor: theme.palette.background.neutral,
                borderRadius: 1,
            }}
            data-cy={`client-admin-roles-table-row-for-${demand.name}`}
        >
            {/* First column: Demand name */}
            <TableCell
                sx={{
                    width: FIRST_COL_WIDTH,
                    ...(isTopLeftCell ? { borderTopLeftRadius: "10px" } : {}),
                    ...(isBottomLeftCell ? { borderBottomLeftRadius: "10px" } : {}),
                }}
                padding="checkbox"
            >
                <Stack direction="row" alignItems="center" justifyContent="flex-start">
                    {!!demand?.icon && <Iconify icon={demand.icon} height={20} width={20} sx={{ mr: 1 }} />}
                    <Typography
                        variant="body"
                        sx={{ fontWeight: "bold" }}
                        color="text.secondary"
                        className="demand-name"
                    >
                        {demand.purpose}
                    </Typography>
                    <Tooltip
                        title={demand.description}
                        aria-describedby={demand.description}
                        arrow
                        slotProps={{ tooltip: { "data-cy": `role-demand-tooltip-${demand.name}` } }}
                    >
                        <IconButton
                            className="demand-info-icon"
                            aria-label="info"
                            size="small"
                            color="primary"
                            sx={{ padding: "2px", fontSize: "16x" }}
                            data-cy={`role-demand-info-${demand.name}`}
                        >
                            <InfoIcon fontSize="inherit" />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </TableCell>

            {/* Other columns: Demand toggles per role */}
            {roles.map((role, roleIx) => {
                const isTopRightCell =
                    groupDemandIx === 0 && roleIx === roles?.length - 1 && roles?.length === ROLES_TO_DISPLAY
                const isBottomRightCell =
                    groupDemandIx === groups[group]?.length - 1 &&
                    roleIx === roles?.length - 1 &&
                    roles?.length === ROLES_TO_DISPLAY
                const isSelected = role.demands.includes(demand.name)
                return (
                    <RolesTableGroupDemandCell
                        key={role.id}
                        isTopRightCell={isTopRightCell}
                        isBottomRightCell={isBottomRightCell}
                        isSelected={isSelected}
                        demand={demand}
                        role={role}
                    />
                )
            })}

            {roles?.length < ROLES_TO_DISPLAY && (
                <>
                    {Array.from({ length: ROLES_TO_DISPLAY - roles?.length }).map((_, ix, missing) => {
                        const isTopRight = groupDemandIx === 0 && ix === missing.length - 1
                        const isBottomRight =
                            groupDemandIx === groups[group].length - 1 &&
                            ix === missing.length - 1 &&
                            !roles.length < ROLES_TO_DISPLAY

                        return (
                            <RolesTableGroupDemandCell
                                key={ix}
                                isTopRightCell={isTopRight}
                                isBottomRightCell={isBottomRight}
                                empty={true}
                            />
                        )
                    })}
                </>
            )}
        </TableRow>
    )
}

async function handleUpdateRoleDemands(role, demand, client) {
    const setDemands = new Set(role.demands)
    if (setDemands.has(demand.name)) {
        setDemands.delete(demand.name)
    } else {
        setDemands.add(demand.name)
    }
    const updatedDemands = [...setDemands]
    await updateRole({ roleId: role.id, roleDemands: updatedDemands, client })
}
